import type { UserLocation } from "@/hooks/find-my-school/use-user-location";
import type {
  IFindMySchoolMapLevelBoundariesParams,
  IFindMySchoolMapLevelBoundaryItem,
  IFindMySchoolSchoolItem,
  IFindMySchoolSchoolsParams,
  SchoolType,
} from "@/providers/store/api/find-my-school/find-my-school.interface";
import type { FindMySchoolSidebarMode } from "@/shared/utils/find-my-school-mode-tabs.util";
import { sortFindMySchoolSchools } from "@/shared/utils/find-my-school-sort.util";


export function getCommittedAddress(
  userLocation: UserLocation | null,
  searchedAddress: string | null,
): string | null {
  if (!userLocation) return null;
  const normalizedSearchedAddress = searchedAddress?.trim();
  if (normalizedSearchedAddress) return normalizedSearchedAddress;
  const normalizedFormattedAddress = userLocation.formattedAddress?.trim();
  if (normalizedFormattedAddress) return normalizedFormattedAddress;
  return null;
}

export function getDefaultSchoolId(
  schools: readonly IFindMySchoolSchoolItem[],
  selectedCategory: SchoolType,
): string | null {
  if (selectedCategory === "all") {
    const elementarySchool = schools.find((school) =>
      school.schoolTypes.includes("ELEMENTARY"),
    );
    if (elementarySchool) return elementarySchool.id;
  }
  return schools[0]?.id ?? null;
}

export function schoolResultsMatchRequest(
  queryArgs: IFindMySchoolSchoolsParams | undefined,
  districtId: string | null,
  userLocation: { lat: number; lng: number } | null,
  selectedCategory: SchoolType,
): boolean {
  if (
    !queryArgs ||
    !districtId ||
    queryArgs.districtId !== districtId ||
    queryArgs.category !== selectedCategory
  ) {
    return false;
  }
  if (!userLocation) {
    return queryArgs.lat === undefined && queryArgs.lng === undefined;
  }
  return (
    queryArgs.lat === userLocation.lat && queryArgs.lng === userLocation.lng
  );
}

export function communityResultsMatchRequest(
  queryArgs: IFindMySchoolMapLevelBoundariesParams | undefined,
  districtId: string | null,
  userLocation: { lat: number; lng: number } | null,
): boolean {
  if (!queryArgs || !districtId || queryArgs.districtId !== districtId) {
    return false;
  }
  if (!userLocation) {
    return queryArgs.lat === undefined && queryArgs.lng === undefined;
  }
  return (
    queryArgs.lat === userLocation.lat && queryArgs.lng === userLocation.lng
  );
}

export function getDesktopSidebarTitle(
  mode: FindMySchoolSidebarMode,
  hasSearchedAddress: boolean,
): string {
  if (mode === "community") {
    return hasSearchedAddress
      ? "Community & services for this address"
      : "Community & services";
  }
  return hasSearchedAddress ? "Browse Schools for:" : "Browse Schools";
}

export function getDistrictCenter(districtData: {
  centerLatitude: number | null;
  centerLongitude: number | null;
}): { lat: number; lng: number } | null {
  if (districtData.centerLatitude == null || districtData.centerLongitude == null) {
    return null;
  }
  return {
    lat: districtData.centerLatitude,
    lng: districtData.centerLongitude,
  };
}

export function getDisplayedSchoolItems(
  schoolItems: readonly IFindMySchoolSchoolItem[],
  selectedCategory: SchoolType,
): IFindMySchoolSchoolItem[] {
  if (selectedCategory === "all") {
    return sortFindMySchoolSchools(schoolItems);
  }
  return [...schoolItems];
}

export function getHighlightedBoundaryBbox(
  communityItems: readonly IFindMySchoolMapLevelBoundaryItem[],
  highlightedBoundaryId: string | null,
): number[] | null {
  if (!highlightedBoundaryId) return null;
  return (
    communityItems.find((item) => item.boundaryId === highlightedBoundaryId)?.bbox ?? null
  );
}

export function resolveNextHighlightedSchoolId(params: {
  isMobileViewport: boolean;
  hasAddressResults: boolean;
  hasSearchedAddress: boolean;
  isSchoolListLoading: boolean;
  isSchoolsLoading: boolean;
  selectedCategory: SchoolType;
  outsideDistrictBoundary: boolean;
  displayedSchoolItems: readonly IFindMySchoolSchoolItem[];
  currentSchoolId: string | null;
}): string | null | undefined {
  const {
    isMobileViewport,
    hasAddressResults,
    hasSearchedAddress,
    isSchoolListLoading,
    isSchoolsLoading,
    selectedCategory,
    outsideDistrictBoundary,
    displayedSchoolItems,
    currentSchoolId,
  } = params;

  const addressResultsReady = isMobileViewport ? hasAddressResults : hasSearchedAddress;
  const schoolResultsLoading = isMobileViewport ? isSchoolListLoading : isSchoolsLoading;
  if (schoolResultsLoading || !addressResultsReady) return undefined;
  if (!isMobileViewport && selectedCategory !== "all") return undefined;
  if (outsideDistrictBoundary || displayedSchoolItems.length === 0) return null;
  if (!isMobileViewport) return displayedSchoolItems[0]?.id ?? null;

  const selectedSchoolIsVisible = displayedSchoolItems.some(
    (school) => school.id === currentSchoolId,
  );
  if (selectedSchoolIsVisible) return currentSchoolId;
  return getDefaultSchoolId(displayedSchoolItems, selectedCategory);
}

export function buildSchoolsQueryArgs(params: {
  districtId: string;
  selectedCategory: SchoolType;
  userLocation: { lat: number; lng: number } | null;
}): IFindMySchoolSchoolsParams {
  const { districtId, selectedCategory, userLocation } = params;
  if (!userLocation) {
    return { districtId, category: selectedCategory };
  }
  return {
    districtId,
    category: selectedCategory,
    lat: userLocation.lat,
    lng: userLocation.lng,
  };
}

export function buildCommunityQueryArgs(params: {
  districtId: string;
  userLocation: { lat: number; lng: number } | null;
}): IFindMySchoolMapLevelBoundariesParams {
  const { districtId, userLocation } = params;
  if (!userLocation) return { districtId };
  return {
    districtId,
    lat: userLocation.lat,
    lng: userLocation.lng,
  };
}
